<template>
  <div class="member-navi">
    <v-app-bar-nav-icon @click="toggleDrawer" />

    <v-navigation-drawer v-model="drawerProxy" temporary width="280">
      <div class="drawer-header">
        <div class="user-box">
          <div class="user-icon">
            <v-icon size="24" color="white">mdi-domain</v-icon>
          </div>
          <div class="user-details">
            <span class="name">{{ currentUserName }}</span>
            <span class="sub">{{ bizGubunName }}</span>
          </div>
        </div>
      </div>

      <v-divider />

      <v-list nav density="comfortable" class="menu-list" :opened="openedGroups">
        <template v-for="item in menuItems" :key="item.title">
          <!-- 하위 메뉴 없는 경우 -->
          <v-list-item
            v-if="!item.children"
            :to="item.to"
            :prepend-icon="item.icon"
            :title="item.title"
            class="menu-item"
            rounded="lg"
            @click="drawerProxy = false"
          />

          <!-- 1단계 하위 메뉴 있는 경우 -->
          <v-list-group v-else :value="item.title">
            <template v-slot:activator="{ props }">
              <v-list-item
                v-bind="props"
                :prepend-icon="item.icon"
                :title="item.title"
                class="menu-item"
                rounded="lg"
              />
            </template>
            
            <template v-for="child in item.children" :key="child.title">
              <!-- 2단계 하위 메뉴 없는 경우 -->
              <v-list-item
                v-if="!child.children"
                :to="child.to"
                :prepend-icon="child.icon"
                :title="child.title"
                class="menu-item sub-item"
                rounded="lg"
                @click="drawerProxy = false"
              />
              
              <!-- 2단계 하위 메뉴 있는 경우 -->
              <v-list-group v-else :value="child.title" sub-group>
                <template v-slot:activator="{ props }">
                  <v-list-item
                    v-bind="props"
                    :prepend-icon="child.icon"
                    :title="child.title"
                    class="menu-item sub-item"
                    rounded="lg"
                  />
                </template>
                <v-list-item
                  v-for="subChild in child.children"
                  :key="subChild.title"
                  :to="subChild.to"
                  :prepend-icon="subChild.icon"
                  :title="subChild.title"
                  class="menu-item sub-sub-item"
                  rounded="lg"
                  @click="drawerProxy = false"
                />
              </v-list-group>
            </template>
          </v-list-group>
        </template>
      </v-list>

      <template v-slot:append>
        <div class="drawer-footer">
          <span class="footer-logo">CAION</span>
        </div>
      </template>
    </v-navigation-drawer>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useRoute } from 'vue-router';
import type { NavItem } from '@/config/navigation';

interface Props {
  modelValue: boolean;
  menuItems: NavItem[];
  currentUserName: string;
  bizGubunName: string;
}

const props = defineProps<Props>();
const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void;
}>();

const route = useRoute();

const drawerProxy = computed({
  get: () => props.modelValue,
  set: (v: boolean) => emit('update:modelValue', v)
});

const toggleDrawer = () => {
  drawerProxy.value = !drawerProxy.value;
};

// 현재 경로에 해당하는 메뉴 그룹을 자동으로 열기
const openedGroups = computed(() => {
  const currentPath = route.path;
  const opened: string[] = [];
  
  const findOpenGroups = (items: NavItem[], parentTitle?: string) => {
    for (const item of items) {
      if (item.children) {
        const hasActiveChild = item.children.some(child => {
          if (child.to === currentPath) return true;
          if (child.children) {
            return child.children.some(sub => sub.to === currentPath);
          }
          return false;
        });
        
        if (hasActiveChild) {
          opened.push(item.title);
          // 2단계 하위 메뉴도 확인
          for (const child of item.children) {
            if (child.children?.some(sub => sub.to === currentPath)) {
              opened.push(child.title);
            }
          }
        }
      }
    }
  };
  
  findOpenGroups(props.menuItems);
  return opened;
});
</script>
